import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminResponse, AdminType } from '../types/auth';
import LanguageSwitcher from './LanguageSwitcher';
import './Navbar.css';

interface NavbarProps {
  onLogout: () => void;
  currentAdmin: AdminResponse | null;
}

const Navbar: React.FC<NavbarProps> = ({ onLogout, currentAdmin }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isSuperAdmin = currentAdmin?.adminType === AdminType.SUPER_ADMIN;

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { path: '/orders', label: t('navbar.orders'), icon: '📋' },
    { path: '/restaurants', label: t('navbar.restaurants'), icon: '🏪' },
    { path: '/drivers', label: t('navbar.drivers'), icon: '🚗' },
    { path: '/clients', label: t('navbar.clients'), icon: '👥' },
    { path: '/reviews', label: t('navbar.reviews'), icon: '⭐' },
    { path: '/notifications', label: t('navbar.notifications'), icon: '🔔' },
    { path: '/search-drivers', label: t('navbar.searchDrivers'), icon: '🔍' },
    { path: '/slots', label: t('navbar.slots'), icon: '🗓️' },
    ...(isSuperAdmin ? [
      { path: '/settings', label: t('navbar.settings'), icon: '⚙️' },
      { path: '/admins', label: t('navbar.admins'), icon: '👨‍💼' },
      { path: '/statistics', label: t('navbar.statistics'), icon: '📊' }
    ] : []),
  ];

  return (
    <>
      <button
        className="mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? '✕' : '☰'}
      </button>

      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <nav className={`navbar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="navbar-header">
          <h2>{t('navbar.appName')}</h2>
          <LanguageSwitcher />
        </div>

        <div className="navbar-menu">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
        </div>

        <div className="navbar-footer">
          {currentAdmin && (
            <div className="admin-info">
              <div className="admin-details">
                <div className="admin-name">{currentAdmin.name}</div>
                <div className="admin-role">
                  {currentAdmin.adminType === AdminType.SUPER_ADMIN ? t('navbar.superAdmin') : t('navbar.admin')}
                </div>
                <div className="admin-email">{currentAdmin.email}</div>
              </div>
            </div>
          )}
          <button onClick={() => { onLogout(); handleLinkClick(); }} className="logout-button">
            <span className="nav-icon">🚪</span>
            <span className="nav-label">{t('navbar.logout')}</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
